// src/api/api.js
// ============================================================
// All API calls to Flask backend
// Base URL: http://localhost:5000/api (proxied via package.json)
// ============================================================
import axios from "axios";

const BASE = "/api";

const api = axios.create({ baseURL: BASE });

// Auto-attach JWT token
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-logout on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

export const authAPI = {
  login: (u, p) => api.post("/auth/login", { username: u, password: p }),
  changePassword: (cur, nw) =>
    api.post("/auth/change-password", {
      current_password: cur,
      new_password: nw,
    }),
};

export const dashboardAPI = {
  get: () => api.get("/dashboard"),
};

export const classAPI = {
  list: (params) => api.get("/classes", { params }), // pass active_only etc
  create: (d) => api.post("/classes", d),
  update: (id, d) => api.put(`/classes/${id}`, d),
};

export const groupAPI = {
  list: () => api.get("/groups"),
  create: (d) => api.post("/groups", d),
  update: (id, d) => api.put(`/groups/${id}`, d),
  assignClass: (d) => api.post("/groups/assign-class", d),
  history: (classId) => api.get(`/groups/assignment-history/${classId}`),
};

export const menuAPI = {
  list: (params) => api.get("/menu-items", { params }),
  create: (data) => api.post("/menu-items", data),
  update: (id, data) => api.put(`/menu-items/${id}`, data),
};

export const configAPI = {
  groupItems: (gid) =>
    api.get("/config/group-items", { params: { group_id: gid } }),
  setGroupItem: (d) => api.post("/config/group-items", d),
  deleteGroupItem: (id) => api.delete(`/config/group-items/${id}`),
  setClassItem: (d) => api.post("/config/class-items", d),
  effective: (cid) => api.get(`/config/effective/${cid}`),
};

export const attendanceAPI = {
  save: (d) => api.post("/attendance", d),
  byDate: (date) => api.get(`/attendance/${date}`),
  monthly: (y, m) => api.get(`/attendance/month/${y}/${m}`),
};

export const stockAPI = {
  get: (y, m) => api.get("/grain-stock", { params: { year: y, month: m } }),
  save: (d) => api.post("/grain-stock", d),
};

export const reportAPI = {
  daily: (date) =>
    api.get(`/reports/daily-summary/${date}`),

  monthly: (year, month) =>
    api.get("/reports/monthly", { params: { year, month } }),

  annual: (academicYearId) =>
    api.get("/reports/academic-year-summary", {
      params: { academic_year_id: academicYearId },
    }),

  classwise: (classId, fromDate, toDate) =>
    api.get("/reports/classwise", {
      params: { class_id: classId, from_date: fromDate, to_date: toDate },
    }),

  monthlyDetail: (y, m) =>
    api.get("/reports/monthly-detail", { params: { year: y, month: m } }),

  classList: () => api.get("/classes/list"),
};

export const visitorAPI = {
  list: (p) => api.get("/visitors", { params: p }),
  add: (d) => api.post("/visitors", d),
};

export const schoolAPI = {
  get: () => api.get("/school"),
  update: (d) => api.put("/school", d),
};

export const yearAPI = {
  list: () => api.get("/academic-years"),
  create: (d) => api.post("/academic-years", d),
  update: (id, d) => api.put(`/academic-years/${id}`, d),
  remove: (id) => api.delete(`/academic-years/${id}`),
};

export default api;
